import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { makeStyles } from "@mui/styles";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
	MoreVert,
	Replay,
	Login,
	CheckCircle,
	TaskAlt,
	Undo,
} from "@mui/icons-material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginLeft: "auto",
		flex: "none",
		alignSelf: "center",
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		"& > *": {
			margin: 0,
		},
	},
	smallButton: {
		padding: "6px 12px",
		fontSize: "0.85rem",
		minWidth: 0,
		minHeight: 34,
		lineHeight: 1.2,
	},
	menuButton: {
		color: "#667781",
	},
	acceptAction: {
		color: "#00a884",
	},
	resolveAction: {
		color: "#d32f2f",
	},
	transferAction: {
		color: "#1976d2",
	},
}));

const TicketActionButtons = ({
	ticket,
	onRefresh,
	lockedByOtherUser = false,
	onStatusChangeSuccess,
	variant = "default",
	allowResolvePending = false,
}) => {
	const classes = useStyles();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
    const ticketPathId = ticket.publicId || ticket.id;
	const isCompact = variant === "compact";
	const missingWhatsapp = Boolean(ticket?.missingWhatsapp);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const renderCompactAction = ({
		title,
		icon,
		onClick,
		disabled = false,
		className,
	}) => (
		<Tooltip title={title}>
			<span>
				<IconButton
					size="small"
					onClick={onClick}
					disabled={disabled || loading}
					className={className || classes.menuButton}
				>
					{icon}
				</IconButton>
			</span>
		</Tooltip>
	);

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			const previousStatus = ticket.status;
			await api.put(`/tickets/${ticketPathId}`, {
				status: status,
				userId: userId || null,
			});

			if (typeof onStatusChangeSuccess === "function") {
				let actionType = "updated";
				if (previousStatus === "pending" && status === "open") {
					actionType = "accepted";
				} else if (previousStatus === "open" && status === "closed") {
					actionType = "resolved";
				} else if (previousStatus === "open" && status === "pending") {
					actionType = "returned";
				} else if (previousStatus === "closed" && status === "open") {
					actionType = "reopened";
				}

				onStatusChangeSuccess({
					type: actionType,
					ticketId: ticketPathId,
					fromStatus: previousStatus,
					toStatus: status,
				});
			}

			setLoading(false);
			if (status === "open") {
				if (onRefresh) {
					onRefresh();
				}
				navigate({ pathname: `/tickets/${ticketPathId}`, state: { forceTab: "open" } });
			} else {
				navigate("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	return (
		<div className={classes.actionButtons}>
			{ticket.status === "closed" && (
				isCompact ? (
					renderCompactAction({
						title: i18n.t("messagesList.header.buttons.reopen"),
						icon: <Replay fontSize="small" />,
						onClick: e => handleUpdateTicketStatus(e, "open", user?.id),
					})
				) : (
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						className={classes.smallButton}
						variant="outlined"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.reopen")}
					</ButtonWithSpinner>
				)
			)}
			{ticket.status === "open" && (
				<>
					{isCompact ? (
						<>
							{renderCompactAction({
								title: i18n.t("messagesList.header.buttons.return"),
								icon: <Undo fontSize="small" />,
								onClick: e => handleUpdateTicketStatus(e, "pending", null),
								disabled: lockedByOtherUser,
							})}
							{renderCompactAction({
								title: i18n.t("messagesList.header.buttons.resolve"),
								icon: <Login fontSize="small" />,
								onClick: e => handleUpdateTicketStatus(e, "closed"),
								disabled: lockedByOtherUser || missingWhatsapp,
								className: classes.resolveAction,
							})}
						</>
					) : (
						<>
							<ButtonWithSpinner
								loading={loading}
								startIcon={<Replay />}
								size="small"
								className={classes.smallButton}
								variant="outlined"
								color="primary"
								onClick={e => handleUpdateTicketStatus(e, "pending", null)}
								disabled={lockedByOtherUser}
							>
								{i18n.t("messagesList.header.buttons.return")}
							</ButtonWithSpinner>
							<ButtonWithSpinner
								variant="contained"
								className={classes.smallButton}
								color="primary"
								onClick={e => handleUpdateTicketStatus(e, "closed")}
								loading={loading}
								disabled={lockedByOtherUser || missingWhatsapp}
							>
								{i18n.t("messagesList.header.buttons.resolve")}
							</ButtonWithSpinner>
						</>
					)}
					{isCompact ? (
						<Tooltip title="Opciones">
							<span>
								<IconButton
									onClick={handleOpenTicketOptionsMenu}
									className={classes.menuButton}
									size="small"
									disabled={lockedByOtherUser || loading}
								>
									<MoreVert />
								</IconButton>
							</span>
						</Tooltip>
					) : (
						<IconButton
							onClick={handleOpenTicketOptionsMenu}
							className={classes.menuButton}
							size="small"
							disabled={lockedByOtherUser}
						>
							<MoreVert />
						</IconButton>
					)}
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
						lockedByOtherUser={lockedByOtherUser}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<>
					{isCompact ? (
						renderCompactAction({
							title: i18n.t("messagesList.header.buttons.accept"),
							icon: <CheckCircle fontSize="small" />,
							onClick: e => handleUpdateTicketStatus(e, "open", user?.id),
							className: classes.acceptAction,
						})
					) : (
						<ButtonWithSpinner
							variant="contained"
							className={classes.smallButton}
							color="primary"
							onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
							loading={loading}
						>
							{i18n.t("messagesList.header.buttons.accept")}
						</ButtonWithSpinner>
					)}
					{allowResolvePending &&
						(isCompact ? (
							renderCompactAction({
								title: i18n.t("messagesList.header.buttons.resolve"),
								icon: <Login fontSize="small" />,
								onClick: e => handleUpdateTicketStatus(e, "closed"),
								disabled: missingWhatsapp,
								className: classes.resolveAction,
							})
						) : (
							<ButtonWithSpinner
								variant="outlined"
								className={classes.smallButton}
								color="primary"
								onClick={e => handleUpdateTicketStatus(e, "closed")}
								loading={loading}
								disabled={missingWhatsapp}
							>
								{i18n.t("messagesList.header.buttons.resolve")}
							</ButtonWithSpinner>
						))}
				</>
			)}
		</div>
	);
};

export default TicketActionButtons;
